import React, { Fragment } from 'react'
import ReactDOMServer from 'react-dom/server'
import qs from 'qs'
import moment from 'moment'
import styled from 'styled-components'
import ContentLoader from 'react-content-loader'
import { OEComponents } from '@opportunityeducation/oe-web-common'
import { postToLoggingService } from '../../../actions/logging'
import Phase from '../../mini/Phase'
import QuestIcon from '../../mini/QuestIcon'
import UserAvatar from '../../mini/UserAvatar'
import FlexContainer from '../../styled/FlexContainer'
import FlexAlignCenter from '../../styled/FlexAlignCenter'
import {
  checkHttpStatus,
  getApiBaseUrlWithOrg,
  getApiHeadersFromToken
} from '../../../utils/api'
import { addActiveField } from '../../../utils/addActiveField'

import {
  H5,
  Caption1,
  Caption2,
  Caption4,
  Caption6,
  Caption5
} from '../../styled/typography/typography'
import LevelCaption from './LevelCaption'

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px 20px;
  gap: 4px;
  background: ${props => props.color};
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
`

const Body = styled.div`
  margin: 20px;
  background: ${props => props.theme.cardBackground} !important;

  ${Caption6} {
    margin: 15px 0 5px;
  }
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.11);
`

const Arrow = styled.div`
  width: 30px;
  height: 15px;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    background-color: ${props => (props.theme === 'dark' ? '#172029' : '#fff')};
    transform: translateX(-50%) translateY(-50%) rotate(45deg);
    bottom: 0;
    left: 50%;
    box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.1);
  }
`

function logViewResponse(response, dataPoint, isMentor) {
  try {
    const data = {
      action: 'view_response',
      student: isMentor
        ? {
            id: dataPoint.student.id,
            name: dataPoint.student.name
          }
        : null,
      skill: {
        id: dataPoint.skill.id,
        name: dataPoint.skill.name
      },
      self_assessment: response.self_assessment,
      quest: response.quest
        ? {
            id: response.quest.id,
            name: response.quest.name
          }
        : null,
      course: response.course
        ? {
            id: response.course.id,
            name: response.course.name
          }
        : null,
      course_section: response.course_section
        ? {
            id: response.course_section.id,
            name: response.course_section.name
          }
        : null,
      master_course: response.master_course
        ? {
            id: response.master_course.id,
            name: response.master_course.name
          }
        : null,
      has_internal_note: !!response.mentors_note,
      has_student_note: !!response.student_note,
      skill_value: parseFloat(response.rating),
      skill_degree_of_mastery: dataPoint.masteryLevel,
      skill_check_date: moment.unix(response.assessed_at).format('YYYY-MM-DD'),
      skill_framework_version: 2
    }

    dataPoint.dispatch(postToLoggingService('skill_check', data))
  } catch (e) {}
}

const createCustomTooltip = (apiData, student, isMentor, lexicon, theme) =>
  async function (context) {
    const tooltipModel = context.tooltip
    if (tooltipModel.dataPoints && tooltipModel.opacity > 0) {
      const dataPoint = tooltipModel.dataPoints[0].raw

      // Tooltip Element
      let tooltipEl = document.getElementById('chartjs-tooltip')

      // Create element on first render
      if (!tooltipEl) {
        tooltipEl = document.createElement('div')
        tooltipEl.id = 'chartjs-tooltip'
        tooltipEl.innerHTML =
          '<div class="tt-inner" style="position:relative"></div>'
        document.body.appendChild(tooltipEl)
      }

      // Set caret Position
      tooltipEl.classList.remove('above', 'below', 'no-transform')
      // tooltipEl.classList.add('below')
      tooltipEl.style.borderTop = '12px solid ' + dataPoint.color

      const position = context.chart.canvas.getBoundingClientRect()

      const innerRoot = tooltipEl.querySelector('.tt-inner')
      innerRoot.innerHTML = ReactDOMServer.renderToString(
        <ContentLoader
          height={90}
          width={450}
          N
          speed={2}
          primaryColor="#F1F1F1"
          secondaryColor="#FBFBFB"
          style={{ padding: '20px' }}
        >
          <rect x="0" y="0" rx="3" ry="3" width="30" height="30" />
          <rect x="40" y="10" rx="3" ry="3" width="130" height="10" />
          <rect x="0" y="50" rx="3" ry="3" width="400" height="10" />
          <rect x="0" y="80" rx="3" ry="3" width="300" height="10" />
        </ContentLoader>
      )

      // Display, position, and set styles for font
      tooltipEl.style.boxShadow = '0 1px 10px 0 rgba(0,0,0,0.1)'
      tooltipEl.style.height = 'auto'
      tooltipEl.style.width = '470px'
      tooltipEl.style.borderRadius = '5px'
      tooltipEl.style.opacity = 1
      tooltipEl.style.position = 'absolute'
      tooltipEl.style.left =
        position.left + window.pageXOffset + tooltipModel.caretX - 235 > 0
          ? position.left + window.pageXOffset + tooltipModel.caretX - 235 <
            window.innerWidth
            ? position.left +
              window.pageXOffset +
              tooltipModel.caretX -
              235 +
              'px'
            : window.innerWidth - 480 + 'px'
          : 10 + 'px'
      tooltipEl.style.top =
        position.top +
        window.pageYOffset +
        tooltipModel.caretY -
        tooltipEl.clientHeight -
        30 +
        'px'
      tooltipEl.style.padding = '0'
      tooltipEl.style.pointerEvents = 'none'
      tooltipEl.style.zIndex = 2147483647
      tooltipEl.style.background = theme?.cardBackground

      const params = qs.stringify({
        q: {
          include_mentors_note: true
        }
      })

      const skillTypeEndpoint = dataPoint.isWorkSkill
        ? 'artifact_assessments/work_skills_responses'
        : 'framework_skills/responses'
      const url = isMentor
        ? `${getApiBaseUrlWithOrg(apiData.orgId)}/users/${
            student.id}/${skillTypeEndpoint}/${dataPoint.id}?${params}`
        : `${getApiBaseUrlWithOrg(apiData.orgId)}/user/${skillTypeEndpoint}/${
            dataPoint.id
          }`

      const response = checkHttpStatus(
        await fetch(url, {
          method: 'GET',
          headers: getApiHeadersFromToken(apiData.token)
        })
      )

      const data = await response.json()

      data.self_assessment = data.self_assessment || dataPoint.isWorkSkill
      const masterCourse = dataPoint.isWorkSkill
        ? data.user_compiled_quest.master_course
        : data.master_course
      const isActivityCourse = masterCourse.operational_type === 'mentor_custom'

      logViewResponse(data, dataPoint, isMentor)

      const getContent = () => (
        <>
          <Header color={dataPoint.color}>
            <Caption4 white>{dataPoint.masteryLevel}</Caption4>
            <Caption6 white>{dataPoint.masteryLevelDescription}</Caption6>
          </Header>

          {(data.student_note || (isMentor && data.mentors_note)) && (
            <Body>
              {data.student_note && (
                <>
                  {isMentor && !data.self_assessment && (
                    <Caption6 style={{ color: `${theme?.caption6color}` }}>
                      Feedback for Student
                    </Caption6>
                  )}
                  <Caption1 style={{ color: `${theme?.caption1color}` }}>
                    {data.student_note}
                  </Caption1>
                </>
              )}

              {isMentor && data.mentors_note && (
                <>
                  <Caption6 style={{ color: `${theme?.caption6color}` }}>
                    Internal Note for {lexicon.mentor}s
                  </Caption6>
                  <Caption1 style={{ color: `${theme?.caption1color}` }}>
                    {data.mentors_note}
                  </Caption1>
                </>
              )}
            </Body>
          )}

          <Divider />

          <FlexContainer style={{ padding: '7px 20px' }}>
            {data.quest && (
              <FlexAlignCenter>
                <QuestIcon
                  size={40}
                  quest={data.quest}
                  borderRadius={isActivityCourse ? 5 : 40}
                />

                <div
                  style={{
                    marginLeft: '13px',
                    maxWidth: '320px'
                  }}
                >
                  <LevelCaption
                    metaData={
                      dataPoint.isWorkSkill ? data.user_compiled_quest : data
                    }
                  />
                  <Caption6 style={{ color: `${theme?.caption6color}` }}>
                    {data.quest.name}
                  </Caption6>
                </div>
              </FlexAlignCenter>
            )}

            <FlexAlignCenter>
              {!data.self_assessment && (
                <UserAvatar
                  size={19}
                  borderRadius={2.5}
                  photoUrl={data.assessor.photo_url}
                  isMentor
                />
              )}

              <Caption5
                style={{ marginLeft: '6px', color: `${theme?.caption1color}` }}
              >
                <OEComponents.DateFormatter
                  date={data.assessed_at}
                  includeTime={false}
                  abbreviateMonth
                />
              </Caption5>
            </FlexAlignCenter>

            <Arrow theme={theme?.style} />
          </FlexContainer>
        </>
      )

      if (tooltipModel.body) {
        const innerHtml = ReactDOMServer.renderToString(getContent())
        const innerRoot = tooltipEl.querySelector('.tt-inner')
        innerRoot.innerHTML = innerHtml
        tooltipEl.style.borderTop = 'none'
        tooltipEl.style.left =
          position.left + window.pageXOffset + tooltipModel.caretX - 235 > 0
            ? position.left + window.pageXOffset + tooltipModel.caretX + 235 <
              window.screen.width
              ? position.left +
                window.pageXOffset +
                tooltipModel.caretX -
                235 +
                'px'
              : window.innerWidth - 480 + 'px'
            : 10 + 'px'
        tooltipEl.style.top =
          position.top +
          window.pageYOffset +
          tooltipModel.caretY -
          tooltipEl.clientHeight -
          30 +
          'px'
      }
    } else {
      const ttEl = document.getElementById('chartjs-tooltip')
      if (ttEl && ttEl.parentNode) {
        ttEl.parentNode.removeChild(ttEl)
      }
    }
  }

export default createCustomTooltip
